import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
// import { FontLoader } from 'three/addons/loaders/FontLoader.js';
// import { TextGeometry } from 'three/addons/geometries/TextGeometry.js';
import { useTheme } from './ThemeContext';

function ThreeScene() {
  const theme = useTheme();
  const isDarkMode = theme.isDarkMode;
  const mountRef = useRef(null);
  const animationIdRef = useRef(null);
  const boidsRef = useRef([]);
  const isDarkModeRef = useRef(isDarkMode);
  const mouseRef = useRef({ x: 9999, y: 9999 }); // Start far away
  const mouseTargetRef = useRef({ x: 9999, y: 9999 });
  const mouseActiveRef = useRef(false);

  useEffect(() => {
    isDarkModeRef.current = isDarkMode;
    boidsRef.current.forEach((boid) => {
      if (!boid.isLeader) {
        boid.updateTheme(isDarkMode);
      }
    });
  }, [isDarkMode]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const isIPhone = /iPhone|iPad|iPod/.test(navigator.userAgent);

    const scene = new THREE.Scene();
    scene.background = null;

    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.set(0, 0, 60);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.outputColorSpace = THREE.SRGBColorSpace;

    const canvas = renderer.domElement;
    canvas.style.display = 'block';
    canvas.style.position = 'absolute';
    canvas.style.inset = '0';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    mount.appendChild(canvas);

    const syncRendererSize = () => {
      const width = mount.clientWidth || window.innerWidth;
      const height = mount.clientHeight || window.innerHeight;
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };

    syncRendererSize();

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.5);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 2.5);
    directionalLight.position.set(20, 20, 20);
    scene.add(directionalLight);

    const blueLight = new THREE.DirectionalLight(0x00BFFF, 2.0);
    blueLight.position.set(-20, -10, -20);
    scene.add(blueLight);

    const pinkLight = new THREE.DirectionalLight(0xFF69B4, 1.5);
    pinkLight.position.set(0, 20, -10);
    scene.add(pinkLight);

    const pointLight1 = new THREE.PointLight(0x87CEEB, 2.5, 100);
    pointLight1.position.set(10, 10, 10);
    scene.add(pointLight1);

    const pointLight2 = new THREE.PointLight(0x9370DB, 2.0, 100);
    pointLight2.position.set(-15, -5, 15);
    scene.add(pointLight2);

    // Rainbow colors for the leader
    const chromeColors = [
      "#00BFFF", "#1E90FF", "#4169E1", "#6A5ACD", "#9370DB",
      "#8A2BE2", "#FF1493", "#FF69B4", "#00CED1", "#20B2AA",
      "#87CEEB", "#FFD700", "#ADFF2F", "#00FF7F", "#FF6347"
    ];

    // CONFIGURATION - 20% weniger Boids
    const BOID_COUNT = isIPhone ? 144 : 280; // Reduced by 20%
    
    // "Riff" - the area around BCRE8IVE text where boids like to swim
    const REEF_CENTER = new THREE.Vector3(0, 5, 0); // Slightly above center (where text is)
    const REEF_RADIUS_X = 45; // Wide area for the text
    const REEF_RADIUS_Y = 15; // Height around text
    // const REEF_RADIUS_Z = 20; // Depth
    
    // Speed settings
    const MIN_SPEED = 0.03; // Minimum speed - always moving
    const CRUISE_SPEED = 0.06; // Normal cruising speed
    const MAX_SPEED = 0.10; // Maximum normal speed
    const FLEE_SPEED = 0.4; // Speed when fleeing from mouse
    const MAX_FORCE = 0.003; // Reduced for smoother movement
    
    // Swarm behavior settings
    const SEPARATION_RADIUS = 3.0;
    const ALIGNMENT_RADIUS = 8;
    const COHESION_RADIUS = 12;
    const SEPARATION_WEIGHT = 1.5;
    const ALIGNMENT_WEIGHT = 1.0;
    const COHESION_WEIGHT = 0.6;
    
    // Mouse avoidance
    const MOUSE_RADIUS = 20; // Detection radius
    const MOUSE_FLEE_WEIGHT = 15.0; // Strong avoidance
    const MIN_MOUSE_DISTANCE = 6; // Mouse can NEVER be this close
    
    // Depth/size settings
    const MIN_Z = -25;
    const MAX_Z = 15;
    const MIN_SCALE = 0.484; // When far (small)
    const MAX_SCALE = 1.089; // When close (big)

    // Available characters from BCRE8IVE
    const availableChars = ['B', 'C', 'R', 'E', '8', 'I', 'V'];
    
    // Geometry cache for each character
    const geometryCache = new Map();
    // let fontLoaded = false;
    // let font = null;
    
    // Load font (using default THREE.js font as fallback)
    // const loader = new FontLoader();
    
    // Try to load a font, fallback to simple geometries if it fails
    // const loadFont = async () => {
    //   try {
    //     // Try loading a standard font - if this fails, we'll use fallback geometries
    //     // For now, we'll create simple geometric representations
    //     fontLoaded = false;
    //   } catch (error) {
    //     console.log('Font loading not available, using geometric fallbacks');
    //     fontLoaded = false;
    //   }
    // };
    
    // Create geometry for a character
    const createCharGeometry = (char) => {
      // Check cache first
      if (geometryCache.has(char)) {
        return geometryCache.get(char);
      }
      
      console.log(`Creating geometry for character: ${char}`);
      let geometry;
      
      // Create simple geometric representations for each character
      switch (char) {
        case '8':
          // Figure-8 shape (rotated 90 degrees)
      const points = [];
      const segments = 48;
      for (let i = 0; i <= segments; i++) {
        const t = (i / segments) * Math.PI * 2;
            const scale = 0.66;
        const denom = 1 + Math.sin(t) * Math.sin(t);
        const x = scale * Math.cos(t) / denom;
        const y = scale * Math.sin(t) * Math.cos(t) / denom;
            // Rotate 90 degrees: (x, y) -> (-y, x)
            points.push(new THREE.Vector3(-y, x, 0));
          }
          const curve = new THREE.CatmullRomCurve3(points, true);
          geometry = new THREE.TubeGeometry(curve, 48, 0.088, 8, true);
          break;
          
        case 'B':
          // B shape - simplified: vertical line with two bulges
          const bPoints = [];
          // Left vertical line
          for (let i = 0; i <= 20; i++) {
            bPoints.push(new THREE.Vector3(-0.25, -0.4 + i * 0.04, 0));
      }
          // Top bulge (semicircle)
          for (let i = 0; i <= 15; i++) {
            const t = (i / 15) * Math.PI;
            bPoints.push(new THREE.Vector3(-0.25 + 0.15 * Math.sin(t), 0.2 + 0.15 * Math.cos(t), 0));
          }
          // Back to middle
          bPoints.push(new THREE.Vector3(-0.25, 0, 0));
          // Bottom bulge (semicircle)
          for (let i = 0; i <= 15; i++) {
            const t = (i / 15) * Math.PI;
            bPoints.push(new THREE.Vector3(-0.25 + 0.15 * Math.sin(t), -0.2 + 0.15 * Math.cos(t), 0));
          }
          // Close back to start
          bPoints.push(new THREE.Vector3(-0.25, -0.4, 0));
          const bCurve = new THREE.CatmullRomCurve3(bPoints, true);
          geometry = new THREE.TubeGeometry(bCurve, 48, 0.088, 8, true);
          break;
          
        case 'C':
          // C shape - open circle (more than half)
          const cPoints = [];
          for (let i = 0; i <= 40; i++) {
            const t = (i / 40) * Math.PI * 1.6 + Math.PI * 0.2;
            cPoints.push(new THREE.Vector3(0.3 * Math.cos(t), 0.3 * Math.sin(t), 0));
          }
          const cCurve = new THREE.CatmullRomCurve3(cPoints, false);
          geometry = new THREE.TubeGeometry(cCurve, 48, 0.088, 8, false);
          break;
          
        case 'R':
          // R shape - vertical line with top curve and diagonal
          const rPoints = [];
          // Vertical line
          for (let i = 0; i <= 20; i++) {
            rPoints.push(new THREE.Vector3(-0.3, -0.5 + i * 0.05, 0));
          }
          // Top curve
          for (let i = 0; i <= 15; i++) {
            const t = (i / 15) * Math.PI * 0.5;
            rPoints.push(new THREE.Vector3(-0.3 + 0.2 * Math.sin(t), 0.3 + 0.2 * Math.cos(t), 0));
          }
          // Middle line
          rPoints.push(new THREE.Vector3(-0.3, 0, 0));
          rPoints.push(new THREE.Vector3(0.1, 0, 0));
          // Diagonal
          for (let i = 0; i <= 15; i++) {
            rPoints.push(new THREE.Vector3(-0.3 + i * 0.026, -0.2 - i * 0.02, 0));
          }
          const rCurve = new THREE.CatmullRomCurve3(rPoints, false);
          geometry = new THREE.TubeGeometry(rCurve, 48, 0.088, 8, false);
          break;
          
        case 'E':
          // E shape - vertical line with three horizontals
          const ePoints = [];
          // Vertical line
          for (let i = 0; i <= 20; i++) {
            ePoints.push(new THREE.Vector3(-0.3, -0.5 + i * 0.05, 0));
          }
          // Top horizontal
          ePoints.push(new THREE.Vector3(-0.3, 0.3, 0));
          ePoints.push(new THREE.Vector3(0.1, 0.3, 0));
          // Middle horizontal
          ePoints.push(new THREE.Vector3(-0.3, 0, 0));
          ePoints.push(new THREE.Vector3(0.1, 0, 0));
          // Bottom horizontal
          ePoints.push(new THREE.Vector3(-0.3, -0.5, 0));
          ePoints.push(new THREE.Vector3(0.1, -0.5, 0));
          const eCurve = new THREE.CatmullRomCurve3(ePoints, false);
          geometry = new THREE.TubeGeometry(eCurve, 48, 0.088, 8, false);
          break;
          
        case 'I':
          // I shape - simple vertical line (very distinct)
          const iPoints = [];
          for (let i = 0; i <= 30; i++) {
            iPoints.push(new THREE.Vector3(0, -0.4 + i * 0.0267, 0));
          }
          const iCurve = new THREE.CatmullRomCurve3(iPoints, false);
          geometry = new THREE.TubeGeometry(iCurve, 48, 0.088, 8, false);
          break;
          
        case 'V':
          // V shape - two diagonal lines meeting at bottom (very distinct)
          const vPoints = [];
          // Left diagonal
          for (let i = 0; i <= 15; i++) {
            vPoints.push(new THREE.Vector3(-0.25 + i * 0.0167, 0.4 - i * 0.06, 0));
          }
          // Right diagonal
          for (let i = 0; i <= 15; i++) {
            vPoints.push(new THREE.Vector3(0.25 - i * 0.0167, -0.5 + i * 0.06, 0));
          }
          const vCurve = new THREE.CatmullRomCurve3(vPoints, false);
          geometry = new THREE.TubeGeometry(vCurve, 48, 0.088, 8, false);
          break;
          
        default:
          // Default to circle
          const circleCurve = new THREE.CatmullRomCurve3([
            new THREE.Vector3(0.3, 0, 0),
            new THREE.Vector3(0, 0.3, 0),
            new THREE.Vector3(-0.3, 0, 0),
            new THREE.Vector3(0, -0.3, 0),
            new THREE.Vector3(0.3, 0, 0)
          ], true);
          geometry = new THREE.TubeGeometry(circleCurve, 48, 0.088, 8, true);
      }
      
      // Cache the geometry
      geometryCache.set(char, geometry);
      return geometry;
    };
    
    // Initialize font loading
    // loadFont();

    class Boid {
      constructor(index, isLeader = false, themeIsDark = true) {
        this.isLeader = isLeader;
        this.index = index;
        this.themeIsDark = themeIsDark;
        
        // Assign a random character from availableChars
        this.character = availableChars[Math.floor(Math.random() * availableChars.length)];
        
        // Leader always uses '8'
        if (isLeader) {
          this.character = '8';
        }
        
        // Debug: log character assignment (only first few to avoid spam)
        if (index < 5) {
          console.log(`Boid ${index} (leader: ${isLeader}) gets character: ${this.character}`);
        }
        
        // Get geometry for this character
        const charGeometry = createCharGeometry(this.character);
        
        // Clone geometry to ensure each mesh has its own instance
        // This prevents issues with shared geometry
        const geometryClone = charGeometry.clone();
        
        // Leader is colored, others adapt to theme: white in dark mode, black in light mode
        const baseColor = isLeader ? chromeColors[0] : (themeIsDark ? "#FFFFFF" : "#000000");
        
        this.material = new THREE.MeshStandardMaterial({
          color: baseColor,
          metalness: 0.85,
          roughness: 0.1,
          envMapIntensity: 2.5,
          emissive: baseColor,
          emissiveIntensity: isLeader ? 0.3 : 0.1,
        });

        this.mesh = new THREE.Mesh(geometryClone, this.material);
        
        // Start position - around the reef
        this.position = new THREE.Vector3(
          REEF_CENTER.x + (Math.random() - 0.5) * REEF_RADIUS_X * 1.5,
          REEF_CENTER.y + (Math.random() - 0.5) * REEF_RADIUS_Y * 1.5,
          MIN_Z + Math.random() * (MAX_Z - MIN_Z)
        );
        
        // Initial velocity - always moving
        const angle = Math.random() * Math.PI * 2;
        const speed = CRUISE_SPEED + (Math.random() - 0.5) * 0.02;
        this.velocity = new THREE.Vector3(
          Math.cos(angle) * speed,
          Math.sin(angle) * speed * 0.5,
          (Math.random() - 0.5) * speed * 0.3
        );
        
        this.acceleration = new THREE.Vector3();
        
        // Individual personality traits - subtle variations
        this.personalAngle = Math.random() * Math.PI * 2; // Personal direction bias
        this.personalSpeed = 0.9 + Math.random() * 0.2; // 0.9-1.1 speed multiplier
        this.reactionDelay = Math.random() * 0.3; // Slight delay in reactions
        this.wobblePhase = Math.random() * Math.PI * 2;
        this.wobbleSpeed = 0.8 + Math.random() * 0.4;
        this.depthPreference = (Math.random() - 0.5) * 10; // Preferred Z depth offset
        this.colorOffset = Math.random() * chromeColors.length;
        this.colorSpeed = 0.03 + Math.random() * 0.02;
        
        // For leader following
        this.leaderFollowWeight = isLeader ? 0 : 0.5 + Math.random() * 0.5;
        
        this.mesh.position.copy(this.position);
        this.updateScale();
      }

      updateScale() {
        // Scale based on Z position (depth)
        const zNormalized = (this.position.z - MIN_Z) / (MAX_Z - MIN_Z);
        const scale = MIN_SCALE + zNormalized * (MAX_SCALE - MIN_SCALE);
        this.mesh.scale.setScalar(scale);
      }

      getDepthScale() {
        const zNormalized = (this.position.z - MIN_Z) / (MAX_Z - MIN_Z);
        return MIN_SCALE + zNormalized * (MAX_SCALE - MIN_SCALE);
      }

      applyForce(force) {
        this.acceleration.add(force);
      }

      // Separation - avoid crowding neighbors
      separate(boids) {
        const steer = new THREE.Vector3();
        let count = 0;

        for (const other of boids) {
          if (other === this) continue;
          const d = this.position.distanceTo(other.position);
          if (d > 0 && d < SEPARATION_RADIUS) {
            const diff = new THREE.Vector3().subVectors(this.position, other.position);
            diff.normalize().divideScalar(d * d); // Squared falloff for smoother response
            steer.add(diff);
            count++;
          }
        }

        if (count > 0) {
          steer.divideScalar(count);
          if (steer.length() > 0) {
            steer.normalize().multiplyScalar(CRUISE_SPEED);
            steer.sub(this.velocity);
            steer.clampLength(0, MAX_FORCE);
          }
        }
        return steer;
      }

      // Alignment - steer towards average heading of neighbors
      align(boids) {
        const sum = new THREE.Vector3();
        let count = 0;

        for (const other of boids) {
          if (other === this) continue;
          const d = this.position.distanceTo(other.position);
          if (d > 0 && d < ALIGNMENT_RADIUS) {
            sum.add(other.velocity);
            count++;
          }
        }

        if (count > 0) {
          sum.divideScalar(count).normalize().multiplyScalar(CRUISE_SPEED);
          const steer = new THREE.Vector3().subVectors(sum, this.velocity);
          steer.clampLength(0, MAX_FORCE);
          return steer;
        }
        return new THREE.Vector3();
      }

      // Cohesion - steer towards center of neighbors
      cohesion(boids) {
        const sum = new THREE.Vector3();
        let count = 0;

        for (const other of boids) {
          if (other === this) continue;
          const d = this.position.distanceTo(other.position);
          if (d > 0 && d < COHESION_RADIUS) {
            sum.add(other.position);
            count++;
          }
        }

        if (count > 0) {
          sum.divideScalar(count);
          return this.seek(sum);
        }
        return new THREE.Vector3();
      }

      // Seek a target position
      seek(target) {
        const desired = new THREE.Vector3().subVectors(target, this.position);
        desired.normalize().multiplyScalar(CRUISE_SPEED);
        const steer = new THREE.Vector3().subVectors(desired, this.velocity);
        steer.clampLength(0, MAX_FORCE);
        return steer;
      }

      // Follow the leader (for non-leaders)
      followLeader(leader) {
        if (this.isLeader || !leader) return new THREE.Vector3();
        
        // Calculate distance to leader
        const distToLeader = this.position.distanceTo(leader.position);
        
        // If too far from leader, prioritize catching up
        if (distToLeader > 25) {
          // Strong pull towards leader
          return this.seek(leader.position).multiplyScalar(2.0);
        }
        
        // Stay behind and to the side of leader
        const behindDistance = 4 + Math.sin(this.personalAngle) * 2;
        const sideOffset = Math.cos(this.personalAngle) * 3;
        
        // Get position behind leader based on leader's velocity
        const leaderDir = leader.velocity.clone().normalize();
        const behind = leader.position.clone()
          .sub(leaderDir.multiplyScalar(behindDistance));
        
        // Add side offset
        const perpendicular = new THREE.Vector3(-leaderDir.y, leaderDir.x, 0);
        behind.add(perpendicular.multiplyScalar(sideOffset));
        
        // Add depth variation
        behind.z += this.depthPreference * 0.3;
        
        return this.seek(behind).multiplyScalar(this.leaderFollowWeight * 1.5);
      }

      // Flee from mouse - CRITICAL: must always be faster than mouse
      fleeFromMouse(mousePos) {
        if (!mouseActiveRef.current) return new THREE.Vector3();
        
        const mousePos3D = new THREE.Vector3(mousePos.x, mousePos.y, this.position.z * 0.3);
        const toMouse = new THREE.Vector3().subVectors(mousePos3D, this.position);
        const dist = toMouse.length();
        
        if (dist < MOUSE_RADIUS) {
          // Direction away from mouse
          const fleeDir = toMouse.normalize().multiplyScalar(-1);
          
          // Add personal variation (very subtle)
          const personalVariation = new THREE.Vector3(
            Math.sin(this.personalAngle) * 0.05,
            Math.cos(this.personalAngle) * 0.05,
            0
          );
          fleeDir.add(personalVariation).normalize();
          
          // Strength based on distance - smooth curve
          let strength;
          if (dist < MIN_MOUSE_DISTANCE) {
            // EMERGENCY - move away if too close
            strength = FLEE_SPEED * 3;
            // Directly adjust position for guaranteed escape
            this.position.add(fleeDir.clone().multiplyScalar(1.5));
        } else {
            // Normal flee - smooth exponential falloff
            const proximity = 1 - (dist / MOUSE_RADIUS);
            strength = FLEE_SPEED * proximity * proximity * 2; // Quadratic for smoother response
          }
          
          const desired = fleeDir.multiplyScalar(strength);
          const steer = new THREE.Vector3().subVectors(desired, this.velocity);
          steer.clampLength(0, MAX_FORCE * 15);
          
          return steer;
        }
        
        return new THREE.Vector3();
      }

      // Stay around the reef (bcre8ive text area)
      stayNearReef(time) {
        const steer = new THREE.Vector3();
        
        // Soft boundary - very gently steer back towards reef
        const distFromReefX = Math.abs(this.position.x - REEF_CENTER.x);
        const distFromReefY = Math.abs(this.position.y - REEF_CENTER.y);
        
        if (distFromReefX > REEF_RADIUS_X) {
          const pull = (distFromReefX - REEF_RADIUS_X) * 0.003;
          steer.x = this.position.x > REEF_CENTER.x ? -pull : pull;
        }
        
        if (distFromReefY > REEF_RADIUS_Y) {
          const pull = (distFromReefY - REEF_RADIUS_Y) * 0.003;
          steer.y = this.position.y > REEF_CENTER.y ? -pull : pull;
        }
        
        // Keep in Z bounds - very gentle
        if (this.position.z < MIN_Z + 3) {
          steer.z = 0.005;
        } else if (this.position.z > MAX_Z - 3) {
          steer.z = -0.005;
        }
        
        // Gentle drift towards preferred depth
        const targetZ = (MIN_Z + MAX_Z) / 2 + this.depthPreference;
        steer.z += (targetZ - this.position.z) * 0.0003;
        
        return steer;
      }

      // Leader navigation - leader actively swims in patterns
      leaderNavigation(time) {
        if (!this.isLeader) return new THREE.Vector3();
        
        // Leader swims in a large figure-8 / lemniscate pattern around the text
        const t = time * 0.12; // Speed of the pattern
        
        // Figure-8 target position
        const targetX = Math.sin(t) * 35;
        const targetY = REEF_CENTER.y + Math.sin(t * 2) * 10;
        const targetZ = Math.cos(t * 0.5) * 8;
        
        const target = new THREE.Vector3(targetX, targetY, targetZ);
        
        // Seek the target position
        const desired = new THREE.Vector3().subVectors(target, this.position);
        const dist = desired.length();
        
        if (dist > 0.1) {
          desired.normalize();
          // Slow down when approaching target
          const speed = dist > 10 ? CRUISE_SPEED * 1.5 : CRUISE_SPEED * (dist / 10);
          desired.multiplyScalar(Math.max(speed, MIN_SPEED));
          
          const steer = new THREE.Vector3().subVectors(desired, this.velocity);
          steer.clampLength(0, MAX_FORCE * 2);
          return steer;
        }
        
        return new THREE.Vector3();
      }

      // Wandering behavior for natural movement - follows leader's general direction
      wander(time) {
        // Personal micro-movement for organic feel
        const microX = Math.sin(time * 0.3 + this.personalAngle) * 0.002;
        const microY = Math.cos(time * 0.25 + this.personalAngle * 1.3) * 0.002;
        
        const wander = new THREE.Vector3(
          microX,
          microY,
          Math.sin(time * 0.1 + this.personalAngle * 0.7) * 0.0005
        );
        return wander;
      }

      flock(boids, mousePos, leader, time) {
        // Standard flocking
        const separation = this.separate(boids).multiplyScalar(SEPARATION_WEIGHT);
        const alignment = this.align(boids).multiplyScalar(ALIGNMENT_WEIGHT);
        const cohesion = this.cohesion(boids).multiplyScalar(COHESION_WEIGHT);
        
        // Mouse avoidance - highest priority
        const mouseAvoid = this.fleeFromMouse(mousePos).multiplyScalar(MOUSE_FLEE_WEIGHT);
        
        // Reef attraction (reduced for more freedom)
        const reefAttraction = this.stayNearReef(time).multiplyScalar(0.5);
    
        // Leader navigation (for leader) or leader following (for followers)
        const leaderNav = this.leaderNavigation(time);
        const leaderFollow = this.followLeader(leader);
        
        // Wandering
        const wander = this.wander(time);
        
        // Apply all forces
        this.applyForce(separation);
        this.applyForce(alignment);
        this.applyForce(cohesion);
        this.applyForce(mouseAvoid);
        this.applyForce(reefAttraction);
        this.applyForce(leaderNav);
        this.applyForce(leaderFollow);
        this.applyForce(wander);
      }

      update(time, deltaTime, mousePos) {
        // Apply acceleration with damping to reduce jitter
        this.velocity.add(this.acceleration);
        this.velocity.multiplyScalar(0.98); // Damping
        
        // Check if fleeing (near mouse)
        let isFleeing = false;
        if (mouseActiveRef.current) {
          const mousePos3D = new THREE.Vector3(mousePos.x, mousePos.y, 0);
          const distToMouse = this.position.distanceTo(mousePos3D);
          isFleeing = distToMouse < MOUSE_RADIUS;
        }
        
        // Speed limits
        const currentMinSpeed = MIN_SPEED * this.personalSpeed;
        const currentMaxSpeed = isFleeing ? FLEE_SPEED : MAX_SPEED * this.personalSpeed;
        
        // Ensure minimum speed
        if (this.velocity.length() < currentMinSpeed) {
          if (this.velocity.length() > 0.001) {
            this.velocity.normalize().multiplyScalar(currentMinSpeed);
          } else {
            // Give random direction if stopped
            const angle = Math.random() * Math.PI * 2;
            this.velocity.set(
              Math.cos(angle) * currentMinSpeed,
              Math.sin(angle) * currentMinSpeed,
              (Math.random() - 0.5) * currentMinSpeed * 0.3
            );
          }
        }
        
        // Clamp to max speed
        this.velocity.clampLength(currentMinSpeed, currentMaxSpeed);
        
        // Update position
        const dt = deltaTime / 16.67;
        this.position.add(this.velocity.clone().multiplyScalar(dt));
        
        // Hard boundary checks
        this.position.x = Math.max(-REEF_RADIUS_X * 1.5, Math.min(REEF_RADIUS_X * 1.5, this.position.x));
        this.position.y = Math.max(-REEF_RADIUS_Y * 2, Math.min(REEF_RADIUS_Y * 2, this.position.y));
        this.position.z = Math.max(MIN_Z, Math.min(MAX_Z, this.position.z));
        
        // Reset acceleration
        this.acceleration.set(0, 0, 0);
    
        // Smooth position update for mesh (interpolate)
        this.mesh.position.lerp(this.position, 0.3);
        this.updateScale();
        
        // Very smooth rotation based on velocity
        const speed = this.velocity.length();
        if (speed > 0.001) {
          const targetYaw = Math.atan2(this.velocity.x, this.velocity.z);
          const targetPitch = -Math.asin(Math.min(1, Math.max(-1, this.velocity.y / speed))) * 0.3;
          
          // Very slow rotation smoothing
          this.mesh.rotation.y += (targetYaw - this.mesh.rotation.y) * 0.02;
          this.mesh.rotation.x += (targetPitch - this.mesh.rotation.x) * 0.02;
          
          // Gentle swimming wobble
          const wobble = Math.sin(time * 3 * this.wobbleSpeed + this.wobblePhase) * 0.05;
          this.mesh.rotation.z += (wobble - this.mesh.rotation.z) * 0.05;
        }
        
        // Subtle scale breathing effect
        const baseScale = this.getDepthScale();
        const breathe = 1 + Math.sin(time * 1.5 + this.wobblePhase) * 0.03;
        this.mesh.scale.setScalar(baseScale * breathe);
    
        // Update colors (leader only)
        if (this.isLeader) {
          const colorTime = time * this.colorSpeed + this.colorOffset;
          const colorIndex = Math.floor(colorTime) % chromeColors.length;
          const nextIndex = (colorIndex + 1) % chromeColors.length;
          const t = colorTime % 1;
          
          const color1 = new THREE.Color(chromeColors[colorIndex]);
          const color2 = new THREE.Color(chromeColors[nextIndex]);
          const finalColor = color1.lerp(color2, t);
          
          this.material.color = finalColor;
          this.material.emissive = finalColor.clone().multiplyScalar(0.3);
        }
      }

      updateTheme(themeIsDark) {
        if (!this.isLeader) {
          const newColor = themeIsDark ? "#FFFFFF" : "#000000";
          this.material.color.set(newColor);
          this.material.emissive.set(newColor);
          this.themeIsDark = themeIsDark;
        }
      }
      
      dispose() {
        this.material.dispose();
      }
    }

    // Create boids - leader first
    const boids = [];
    const leader = new Boid(0, true, isDarkModeRef.current); // Leader (colored)
    boids.push(leader);
    scene.add(leader.mesh);

    // Create followers
    for (let i = 1; i < BOID_COUNT; i++) {
      const boid = new Boid(i, false, isDarkModeRef.current);
      boids.push(boid);
      scene.add(boid.mesh);
    }

    boidsRef.current = boids;

    let lastTime = 0;
    let isContextLost = false;

    const animate = (currentTime) => {
      animationIdRef.current = requestAnimationFrame(animate);
      if (isContextLost) return;
      
      const deltaTime = Math.min(currentTime - lastTime, 50);
      lastTime = currentTime;
      const time = currentTime * 0.001;
      
      // Smooth mouse position
      const smoothingFactor = 0.12;
      mouseRef.current.x += (mouseTargetRef.current.x - mouseRef.current.x) * smoothingFactor;
      mouseRef.current.y += (mouseTargetRef.current.y - mouseRef.current.y) * smoothingFactor;
                
      // Convert mouse to world coordinates
      const mousePos = new THREE.Vector3(
        mouseRef.current.x * 50,
        mouseRef.current.y * 30,
        0
      );
      
      // First pass: calculate forces
      for (const boid of boids) {
        boid.flock(boids, mousePos, leader, time);
                }
                
      // Second pass: update positions
      for (const boid of boids) {
        boid.update(time, deltaTime, mousePos);
                }
                
      // Subtle camera movement
      camera.position.x = Math.sin(time * 0.08) * 2;
      camera.position.y = Math.cos(time * 0.06) * 1.5;
      camera.lookAt(0, 0, 0);

      renderer.render(scene, camera);
    };

    animate(0);

    const handleResize = () => {
      syncRendererSize();
    };

    const handleContextLost = (event) => {
      event.preventDefault();
      isContextLost = true;
    };

    const handleContextRestored = () => {
      isContextLost = false;
      syncRendererSize();
      lastTime = 0;
    };

    const handleMouseMove = (e) => {
      mouseActiveRef.current = true;
      mouseTargetRef.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouseTargetRef.current.y = -(e.clientY / window.innerHeight) * 2 + 1;
    };

    const handleMouseLeave = () => {
      mouseActiveRef.current = false;
      mouseTargetRef.current.x = 9999;
      mouseTargetRef.current.y = 9999;
    };

    const handleTouchMove = (e) => {
      if (e.touches.length > 0) {
        mouseActiveRef.current = true;
        mouseTargetRef.current.x = (e.touches[0].clientX / window.innerWidth) * 2 - 1;
        mouseTargetRef.current.y = -(e.touches[0].clientY / window.innerHeight) * 2 + 1;
      }
    };

    const handleTouchEnd = () => {
      mouseActiveRef.current = false;
      mouseTargetRef.current.x = 9999;
      mouseTargetRef.current.y = 9999;
    };

    canvas.addEventListener('webglcontextlost', handleContextLost, false);
    canvas.addEventListener('webglcontextrestored', handleContextRestored, false);
    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      canvas.removeEventListener('webglcontextlost', handleContextLost, false);
      canvas.removeEventListener('webglcontextrestored', handleContextRestored, false);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);

      if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }

      boidsRef.current = [];
      geometryCache.forEach((geometry) => geometry.dispose());
      geometryCache.clear();

      boids.forEach((b) => b.dispose());
      renderer.dispose();
    };
  }, []);

  return (
    <div
      ref={mountRef}
      className="absolute inset-0 h-full w-full"
      style={{ background: 'transparent' }}
    />
  );
}

export default ThreeScene;